import React, { useState } from "react";
import api, { inr, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Lock } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";

const diffTone = (d) => (Math.abs(d) < 0.01 ? "text-[hsl(var(--positive))]" : d < 0 ? "text-destructive" : "text-amber-600");
const diffText = (d) => (Math.abs(d) < 0.01 ? "Matched" : d < 0 ? `Short ${inr(-d)}` : `Extra ${inr(d)}`);

function Row({ name, book, value, setValue, testid }) {
  const d = value === "" ? null : Math.round((parseFloat(value) - book) * 100) / 100;
  return (
    <div className="grid grid-cols-[52px_1fr_130px] sm:grid-cols-[70px_1fr_1fr_1fr] gap-x-2 gap-y-1 items-center text-sm">
      <span className="font-medium">{name}</span>
      <span className="num text-muted-foreground">{inr(book)}</span>
      <input data-testid={`count-${testid}`} type="number" step="0.01" className={field} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Counted" />
      <span data-testid={`count-${testid}-diff`} className={`col-start-2 col-span-2 sm:col-start-auto sm:col-span-1 num text-xs font-semibold ${d === null ? "text-muted-foreground" : diffTone(d)}`}>{d === null ? "—" : diffText(d)}</span>
    </div>
  );
}

export function ClosingCountDialog({ open, onOpenChange, date, balances, onDone }) {
  const [cash, setCash] = useState("");
  const [bank, setBank] = useState("");
  const [reason, setReason] = useState("");
  React.useEffect(() => {
    if (open) { setCash(""); setBank(""); setReason(""); }
  }, [open, date]);
  const cb = balances?.cash_balance || 0, bb = balances?.bank_balance || 0;
  const filled = cash !== "" && bank !== "";
  const mismatch = filled && (Math.abs(parseFloat(cash) - cb) > 0.009 || Math.abs(parseFloat(bank) - bb) > 0.009);

  const submit = async () => {
    if (!filled) return toast.error("Enter counted cash and actual bank balance");
    if (mismatch && !reason.trim()) return toast.error("Enter a reason for the difference");
    try {
      await api.post("/cashbook/lock", { date, cash_counted: parseFloat(cash), bank_counted: parseFloat(bank), reason: reason.trim() });
      toast.success(mismatch ? "Day locked with difference noted" : "Count matched · day locked");
      setCash(""); setBank(""); setReason("");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Closing count · {date}</DialogTitle></DialogHeader>
        <div data-testid="closing-count-dialog" className="space-y-3">
          <div className="grid grid-cols-[52px_1fr_130px] sm:grid-cols-[70px_1fr_1fr_1fr] gap-2 text-xs text-muted-foreground">
            <span /><span>As per books</span><span>Physical / actual</span><span className="hidden sm:block">Difference</span>
          </div>
          <Row name="Cash" book={cb} value={cash} setValue={setCash} testid="cash" />
          <Row name="Bank" book={bb} value={bank} setValue={setBank} testid="bank" />
          {mismatch && (
            <div>
              <label className="block text-sm font-medium mb-1.5">Reason for difference <span className="text-destructive">*</span></label>
              <textarea data-testid="count-reason" rows={2} className="w-full px-3 py-2 rounded-md border border-input bg-card text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. ₹200 change given, bank charges not entered" />
            </div>
          )}
          <p className="text-xs text-muted-foreground">After locking, entries for this date cannot be added, edited or deleted (an accountant/admin can unlock).</p>
        </div>
        <DialogFooter>
          <button data-testid="count-lock-confirm" onClick={submit} className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors">
            <Lock size={15} /> {mismatch ? "Lock with difference" : "Lock day"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ClosingCountSummary({ count }) {
  if (!count) return null;
  return (
    <div data-testid="closing-count-summary" className="rounded-xl border border-border bg-card px-4 py-3 mb-5 text-sm flex flex-wrap items-center gap-x-6 gap-y-1 animate-fade-up">
      <span className="font-medium">Closing count</span>
      <span className="num">Cash {inr(count.cash_counted)} <span className={`text-xs font-semibold ${diffTone(count.cash_diff)}`}>({diffText(count.cash_diff)})</span></span>
      <span className="num">Bank {inr(count.bank_counted)} <span className={`text-xs font-semibold ${diffTone(count.bank_diff)}`}>({diffText(count.bank_diff)})</span></span>
      {count.reason && <span className="text-xs text-muted-foreground">Reason: {count.reason}</span>}
      <span className="text-xs text-muted-foreground">by {count.by}</span>
    </div>
  );
}
